// Update checker utility for automatic version updates
class UpdateChecker {
    constructor() {
        this.currentVersion = 'v1.0.55'; // Should match service worker version
        this.checkInterval = null;
        this.isUpdateAvailable = false;
        this.updateCallback = null;
    }

    // Start checking for updates
    startChecking(callback) {
        this.updateCallback = callback;
        
        // Check immediately
        this.checkForUpdates();
        
        // Check every 5 minutes
        this.checkInterval = setInterval(() => {
            this.checkForUpdates();
        }, 5 * 60 * 1000);
        
        // Listen for service worker updates
        this.listenForServiceWorkerUpdates();
    }

    // Stop checking for updates
    stopChecking() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }

    // Check if there's a new version available
    async checkForUpdates() {
        try {
            // Check if service worker is registered
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration();
                
                if (registration && registration.waiting) {
                    // There's a waiting service worker (new version available)
                    this.handleUpdateAvailable();
                } else if (registration && registration.installing) {
                    // Service worker is installing
                    registration.installing.addEventListener('statechange', (event) => {
                        if (event.target.state === 'installed') {
                            this.handleUpdateAvailable();
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error checking for updates:', error);
        }
    }

    // Listen for service worker updates
    listenForServiceWorkerUpdates() {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                // Service worker has been updated
                this.handleUpdateInstalled();
            });
        }
    }

    // Handle when an update is available
    handleUpdateAvailable() {
        if (!this.isUpdateAvailable) {
            this.isUpdateAvailable = true;
            
            if (this.updateCallback) {
                this.updateCallback({
                    type: 'update_available',
                    message: 'A new version is available!'
                });
            }
        }
    }

    // Handle when update is installed
    handleUpdateInstalled() {
        this.isUpdateAvailable = false;
        
        if (this.updateCallback) {
            this.updateCallback({
                type: 'update_installed',
                message: 'App updated successfully!'
            });
        }
    }

    // Force update (skip waiting and reload)
    async forceUpdate() {
        try {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration();
                
                if (registration && registration.waiting) {
                    // Send message to service worker to skip waiting
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    
                    // Reload the page after a short delay
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                }
            }
        } catch (error) {
            console.error('Error forcing update:', error);
        }
    }

    // Clear cache and reload
    async clearCacheAndReload() {
        try {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration();
                
                if (registration) {
                    // Send message to clear cache
                    registration.active.postMessage({ type: 'CLEAR_CACHE' });
                    
                    // Unregister service worker
                    await registration.unregister();
                    
                    // Clear all caches
                    const cacheNames = await caches.keys();
                    await Promise.all(
                        cacheNames.map(cacheName => caches.delete(cacheName))
                    );
                }
            }
            
            // Reload the page
            window.location.reload();
        } catch (error) {
            console.error('Error clearing cache:', error);
        }
    }

    // Get current version
    getCurrentVersion() {
        return this.currentVersion;
    }

    // Check if update is available
    isUpdateReady() {
        return this.isUpdateAvailable;
    }
}

// Create singleton instance
const updateChecker = new UpdateChecker();

export default updateChecker;
